import React from 'react';
import Button from '../../../components/base/Button';

const treatments = [
  {
    id: 'hormone-therapy',
    title: 'Bioidentical Hormone Therapy',
    description: 'Restore hormonal balance with personalized bioidentical hormone replacement therapy tailored to your unique needs.',
    benefits: ['Improved energy levels', 'Better sleep quality', 'Enhanced mood stability', 'Increased libido'],
    duration: '3-6 months',
    price: 'From $299/month',
    image: 'https://public.readdy.ai/ai/img_res/27b5944fb150c4c07c0661ec80c6aedf.jpg',
    icon: 'ri-heart-pulse-line',
    color: 'bg-pink-500'
  },
  {
    id: 'peptide-therapy',
    title: 'Peptide Therapy',
    description: 'Harness the power of peptides to enhance cellular function, promote healing, and optimize performance.',
    benefits: ['Accelerated recovery', 'Enhanced muscle growth', 'Improved cognitive function', 'Anti-aging effects'],
    duration: '2-4 months',
    price: 'From $399/month',
    image: 'https://readdy.ai/api/search-image?query=Peptide%20therapy%20medical%20vials%20in%20laboratory%20setting%2C%20scientific%20precision%20equipment%2C%20clean%20white%20background%2C%20modern%20biotechnology%2C%20medical%20research%20environment%2C%20professional%20healthcare%20setup%2C%20minimalist%20design%2C%20advanced%20molecular%20medicine&width=600&height=400&seq=peptide-therapy&orientation=landscape',
    icon: 'ri-dna-line',
    color: 'bg-blue-500'
  },
  {
    id: 'nad-therapy',
    title: 'NAD+ Treatment',
    description: 'Boost cellular energy and support healthy aging with NAD+ supplementation therapy.',
    benefits: ['Increased energy', 'Enhanced mental clarity', 'Improved metabolism', 'Cellular regeneration'],
    duration: '1-3 months',
    price: 'From $249/month',
    image: 'https://readdy.ai/api/search-image?query=NAD%20therapy%20treatment%20setup%2C%20IV%20drip%20equipment%2C%20modern%20medical%20facility%2C%20clean%20white%20environment%2C%20cellular%20regeneration%20therapy%2C%20professional%20healthcare%20setting%2C%20minimalist%20medical%20design%2C%20advanced%20anti-aging%20treatment&width=600&height=400&seq=nad-therapy&orientation=landscape',
    icon: 'ri-flashlight-line',
    color: 'bg-yellow-500'
  },
  {
    id: 'iv-therapy',
    title: 'IV Vitamin Therapy',
    description: 'Direct nutrient delivery through customized IV vitamin infusions for optimal absorption.',
    benefits: ['Rapid nutrient absorption', 'Immune system boost', 'Hydration optimization', 'Energy enhancement'],
    duration: 'Single sessions',
    price: 'From $149/session',
    image: 'https://readdy.ai/api/search-image?query=IV%20vitamin%20therapy%20setup%2C%20medical%20drip%20bag%20and%20equipment%2C%20clean%20white%20medical%20environment%2C%20professional%20healthcare%20facility%2C%20vitamin%20infusion%20treatment%2C%20minimalist%20design%2C%20modern%20medical%20technology%2C%20wellness%20therapy&width=600&height=400&seq=iv-therapy&orientation=landscape',
    icon: 'ri-drop-line',
    color: 'bg-green-500'
  },
  {
    id: 'glutathione-therapy',
    title: 'Glutathione Therapy',
    description: 'Master antioxidant therapy to support detoxification and cellular protection.',
    benefits: ['Powerful antioxidant support', 'Detoxification enhancement', 'Skin health improvement', 'Immune support'],
    duration: '4-8 weeks',
    price: 'From $199/month',
    image: 'https://readdy.ai/api/search-image?query=Glutathione%20therapy%20medical%20setup%2C%20antioxidant%20treatment%20vials%2C%20clean%20white%20laboratory%20environment%2C%20professional%20medical%20facility%2C%20detoxification%20therapy%20equipment%2C%20minimalist%20healthcare%20design%2C%20modern%20wellness%20treatment&width=600&height=400&seq=glutathione-therapy&orientation=landscape',
    icon: 'ri-shield-star-line',
    color: 'bg-purple-500'
  },
  {
    id: 'wellness-plan',
    title: 'Comprehensive Wellness',
    description: 'Complete health optimization program combining multiple therapies for maximum results.',
    benefits: ['Holistic health approach', 'Personalized protocols', 'Ongoing monitoring', 'Lifestyle optimization'],
    duration: '6-12 months',
    price: 'From $599/month',
    image: 'https://readdy.ai/api/search-image?query=Comprehensive%20wellness%20program%20setup%2C%20multiple%20medical%20treatments%2C%20holistic%20healthcare%20environment%2C%20clean%20white%20medical%20facility%2C%20personalized%20medicine%20approach%2C%20professional%20wellness%20center%2C%20minimalist%20design%2C%20advanced%20health%20optimization&width=600&height=400&seq=wellness-plan&orientation=landscape',
    icon: 'ri-heart-3-line',
    color: 'bg-teal-500'
  }
];

const TreatmentGrid: React.FC = () => {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Our <span className="text-teal-600">Treatment Portfolio</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Each treatment is carefully designed and monitored by our medical experts to ensure 
            optimal results and safety for your <strong>personalized wellness journey</strong>.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {treatments.map((treatment) => (
            <div key={treatment.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="relative">
                <img 
                  src={treatment.image} 
                  alt={treatment.title}
                  className="w-full h-48 object-cover object-top"
                />
                <div className={`absolute top-4 left-4 w-12 h-12 ${treatment.color} rounded-full flex items-center justify-center`}>
                  <i className={`${treatment.icon} text-white text-xl`}></i>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{treatment.title}</h3>
                <p className="text-gray-600 mb-4 text-sm leading-relaxed">{treatment.description}</p>
                
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">Key Benefits:</h4>
                  <ul className="space-y-1">
                    {treatment.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-center text-sm text-gray-600">
                        <i className="ri-check-line text-teal-600 mr-2 text-xs"></i>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="flex justify-between items-center mb-4 text-sm">
                  <div>
                    <span className="text-gray-500">Duration:</span>
                    <span className="font-semibold text-gray-900 ml-1">{treatment.duration}</span>
                  </div>
                  <div>
                    <span className="text-teal-600 font-bold">{treatment.price}</span>
                  </div>
                </div>
                
                <Button variant="primary" size="sm" className="w-full">
                  <i className="ri-information-line mr-2"></i>
                  Learn More
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Button variant="outline" size="lg">
            <i className="ri-calendar-check-line mr-2"></i>
            Schedule Free Consultation
          </Button>
        </div>
      </div>
    </section>
  );
};

export default TreatmentGrid;